import { autoinject, bindable, Container } from 'aurelia-framework';
import { SearchResult } from 'elements/search/search-input';
import { PubSub } from 'lib/event/PubSub';
import { AsyncErrorHandler } from 'lib/ui';
import {
  MooringArticleSearchResult,
  MooringProductionBridleDto,
  MooringProductionBridleEditDto,
  MooringProductionBridleSlotDto,
} from 'models/mooring';
import { MooringComponentSearchHandler, MooringService } from 'services/mooring';
import { MooringComponentSearchResult } from 'services/mooring/mooring-component-search-handler';
import { OfferViewContext } from 'views/mooring/mooring-offers/OfferViewContext';

@autoinject()
export class MooringProductionBridlesTable {
  @bindable mooringId!: number;

  protected bridles: MooringProductionBridleDto[] = [];
  protected editRows: MooringProductionBridleEditDto[] | null = null;
  protected isEditing = false;
  protected context = OfferViewContext.useContext(this.container);

  constructor(
    private container: Container,
    private mooringService: MooringService,
    private pubsub: PubSub,
    protected mooringComponentSearchHandler: MooringComponentSearchHandler
  ) {
    this.pubsub.sub('list-entity:updated', () => {
      if (!this.isEditing) void this.getBridles();
    });
    this.pubsub.sub('entity:updated', () => {
      if (!this.isEditing) void this.getBridles();
    });
  }

  protected bind() {
    if (!this.mooringId) {
      throw new Error(`Mooring id is missing.`);
    }
  }

  protected async attached() {
    await this.mooringComponentSearchHandler.preCache();
    await this.getBridles();
  }

  protected async setEdit() {
    if (this.isEditing) {
      await this.getBridles();
      this.editRows = null;
      this.isEditing = false;
      return;
    }

    this.editRows = this.bridles.map(
      (b) =>
        ({
          CageId: b.CageId,
          RopeSlot: b.RopeSlot ?? undefined,
          StrapSlot: b.StrapSlot ?? undefined,
        }) as MooringProductionBridleEditDto
    );
    this.isEditing = true;
  }

  protected onArticleSelected(searchResult: MooringComponentSearchResult, slot: MooringProductionBridleSlotDto) {
    if (!searchResult?.id || !slot) return;
    const article = searchResult.return;
    if (!article) return;
    slot.ArticleProductId = article.Id;
    slot.ProductName = article.ProductName;
  }

  @AsyncErrorHandler
  protected async save() {
    if (!this.editRows) return;
    await this.mooringService.updateProductionBridles(this.mooringId, this.editRows);
    this.editRows = null;
    this.isEditing = false;
    await this.getBridles();
  }

  @AsyncErrorHandler
  private async getBridles() {
    const { data } = await this.mooringService.getProductionBridles(this.mooringId);
    this.bridles = data ?? [];
  }

  protected unbind() {
    this.pubsub.unsub();
  }

  protected detached() {
    this.pubsub.unsub();
  }
}
